/** Browser and server analytics share this policy. Never change the real browser URL. */
const SAFE_QUERY_KEYS = new Set([
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_content',
	'utm_term',
	'page',
	'store',
	'category',
	'sort',
	'tab',
	'risk',
])
const SENSITIVE_KEY =
	/^(?:\$initial_)?(?:(?:access_?token|refresh_?token|id_?token|token)(?:_?plaintext)?|txn|code|code_?verifier|code_?challenge|state|password|authorization|client_?secret|api_?key|redirect|redirect_?uri|return_?url|return_?to|next)$/i
const URL_KEY = /(?:url|uri|href|referrer|referer|path|pathname|location|action)$/i
const JWT = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g
const PRODUCTION_HOSTS = new Set([
	'extenshi.io',
	'www.extenshi.io',
	'catalog.extenshi.io',
	'dojo.extenshi.io',
	'genkan.extenshi.io',
	'auth.extenshi.io',
	'blog.extenshi.io',
	'docs.extenshi.io',
	'privacy.extenshi.io',
	'uninstall-form.extenshi.io',
	'feedback.extenshi.io',
])

export function isProductionAnalyticsHost(host: string): boolean {
	return PRODUCTION_HOSTS.has(host.toLowerCase())
}

export function isInternalAnalyticsUser(user: {
	role?: unknown
	app_metadata?: Record<string, unknown>
}): boolean {
	const internalRole = (role: unknown) =>
		typeof role === 'string' && ['admin', 'owner'].includes(role.toLowerCase())
	return (
		internalRole(user.role) ||
		internalRole(user.app_metadata?.role) ||
		user.app_metadata?.is_internal === true
	)
}

function sanitizePathname(pathname: string): string {
	let decoded = pathname
	for (let i = 0; i < 3 && /%[0-9a-f]{2}/i.test(decoded); i++) {
		try {
			decoded = decodeURIComponent(decoded)
		} catch {
			break
		}
	}
	// This route grants access using the path token itself, without another login.
	return /\/triage\/discussion\//i.test(decoded)
		? decoded.replace(/(\/triage\/discussion\/).*$/i, '$1[redacted]')
		: pathname
}

/** Allowlisted navigation/attribution only; nested redirects, tokens and fragments are discarded. */
export function sanitizeAnalyticsUrl(input: string): string {
	let value = input
	try {
		// Persisted SDK properties can contain a percent-encoded redirect URL.
		for (let i = 0; i < 3 && /%[0-9a-f]{2}/i.test(value) && !/^(?:https?:\/\/|\/)/i.test(value); i++) {
			value = decodeURIComponent(value)
		}
		if (!/^(?:https?:\/\/|\/|\.\/|\.\.\/)/i.test(value)) return value.replace(JWT, '[redacted]')
		const absolute = /^https?:\/\//i.test(value)
		const protocolRelative = value.startsWith('//')
		const url = new URL(value, 'https://analytics.invalid')
		const pathname = sanitizePathname(url.pathname)
		const query = new URLSearchParams()
		for (const [key, parameter] of url.searchParams) {
			if (SAFE_QUERY_KEYS.has(key.toLowerCase())) query.append(key, parameter.replace(JWT, '[redacted]'))
		}
		const base = absolute
			? `${url.protocol}//${url.host}${pathname}`
			: protocolRelative
				? `//${url.host}${pathname}`
				: pathname
		return `${base}${query.size ? `?${query}` : ''}`
	} catch {
		return '[redacted-url]'
	}
}

function sanitizeText(value: string, key: string): string {
	if (URL_KEY.test(key)) return sanitizeAnalyticsUrl(value)
	return value
		.replace(/https?:\/\/[^\s"'<>\\]+/gi, sanitizeAnalyticsUrl)
		.replace(
			/((?:href|src|action)=["'])([^"']+)(["'])/gi,
			(_, prefix: string, url: string, suffix: string) => `${prefix}${sanitizeAnalyticsUrl(url)}${suffix}`,
		)
		.replace(JWT, '[redacted]')
}

/** Includes nested $set/$set_once, exception frames and autocaptured link attributes. */
export function sanitizeAnalyticsProperties<T>(input: T): T {
	const ancestors = new WeakSet<object>()
	const visit = (value: unknown, key: string): unknown => {
		if (SENSITIVE_KEY.test(key)) return '[redacted]'
		if (typeof value === 'string') return sanitizeText(value, key)
		if (value && typeof value === 'object') {
			if (ancestors.has(value)) return '[circular]'
			ancestors.add(value)
			let result: unknown = value
			if (Array.isArray(value)) result = value.map((item) => visit(item, key))
			else if (Object.getPrototypeOf(value) === Object.prototype) {
				result = Object.fromEntries(Object.entries(value).map(([name, item]) => [name, visit(item, name)]))
			}
			ancestors.delete(value)
			return result
		}
		return value
	}
	return visit(input, '') as T
}

type AnalyticsEvent = {
	properties?: Record<string, unknown>
	$set?: Record<string, unknown>
	$set_once?: Record<string, unknown>
}

export function posthogBeforeSend<T extends AnalyticsEvent>(event: T | null): T | null {
	if (!event) return null
	try {
		const host = typeof window !== 'undefined' ? window.location.hostname : event.properties?.$host
		if (typeof host === 'string' && !isProductionAnalyticsHost(host)) return null
		const clean = sanitizeAnalyticsProperties(event)
		// PostHog's public project token is transport metadata, not an auth token.
		// Preserve it only at the SDK-owned envelope location; nested tokens stay masked.
		if (typeof event.properties?.token === 'string' && /^phc_[a-z0-9]+$/i.test(event.properties.token)) {
			clean.properties ??= {}
			clean.properties.token = event.properties.token
		}
		const pathname =
			typeof window !== 'undefined'
				? window.location.pathname
				: (event.properties?.$pathname ?? event.properties?.path)
		if (
			clean.properties?.is_internal === true ||
			clean.$set?.is_internal === true ||
			isInternalAnalyticsUser({ role: clean.properties?.role }) ||
			isInternalAnalyticsUser({ role: clean.$set?.role }) ||
			(typeof pathname === 'string' && /^\/admin(?:\/|$)/.test(pathname))
		) {
			clean.properties ??= {}
			clean.properties.is_internal = true
		}
		return clean
	} catch {
		// SDK hooks can otherwise fall back to the original event after a throw.
		// Drop malformed telemetry rather than sending an unsanitized payload.
		return null
	}
}

/** Replay metadata/DOM/network URLs have their own masking path, separate from before_send. */
export const posthogPrivacyConfig = {
	before_send: posthogBeforeSend,
	get_current_url: (defaultUrl: string) => sanitizeAnalyticsUrl(defaultUrl),
	disable_capture_url_hashes: true,
	// Replay console plugins bypass event before_send and may contain full auth redirects.
	enable_recording_console_log: false,
	session_recording: {
		maskAllInputs: true,
		recordHeaders: false,
		recordBody: false,
		maskAttributeFn: (name: string, value: string) => sanitizeText(value, name),
		maskCapturedNetworkRequestFn: <T extends { name: string }>(request: T): T => ({
			...request,
			name: sanitizeAnalyticsUrl(request.name),
		}),
	},
}

/**
 * Hold only already-consented product actions during the child-effect/SDK-init
 * race. The queue is memory-only, capped at 20 actions and five seconds each.
 * Withdrawal drops it permanently; subsequent consent never replays old actions.
 */
export function createConsentSafeCapture(options: {
	hasConsent: () => boolean
	isReady: () => boolean
	send: (event: string, properties: Record<string, unknown>, observedAt: Date) => void
	getTarget?: () => EventTarget | undefined
}): (event: string, properties?: Record<string, unknown>) => void {
	type Pending = { event: string; properties: Record<string, unknown>; observedAt: number }
	const maxPending = 20
	const maxWaitMs = 5000
	let pending: Pending[] = []
	let target: EventTarget | undefined
	let timer: ReturnType<typeof setTimeout> | undefined
	const getTarget = options.getTarget ?? (() => (typeof window === 'undefined' ? undefined : window))

	function clear() {
		pending = []
		if (timer !== undefined) clearTimeout(timer)
		timer = undefined
		target?.removeEventListener('posthog-initialized', flush)
		target?.removeEventListener('cookie-consent-updated', onConsentUpdate)
		target = undefined
	}

	function send(item: Pending) {
		try {
			options.send(item.event, item.properties, new Date(item.observedAt))
		} catch {
			// Telemetry failures must not interrupt the product or other actions.
		}
	}

	function flush() {
		try {
			if (!options.hasConsent()) {
				clear()
				return
			}
			if (!options.isReady()) return
			const actions = pending
			clear() // Pop before sending so repeated/reentrant init signals cannot replay.
			for (const item of actions) {
				if (!options.hasConsent()) break
				// Background tabs can throttle the expiry timer, so check TTL here too.
				if (Date.now() - item.observedAt < maxWaitMs) send(item)
			}
		} catch {
			clear()
		}
	}

	function onConsentUpdate() {
		try {
			if (!options.hasConsent()) clear()
		} catch {
			clear()
		}
	}

	function expire() {
		pending = pending.filter((item) => Date.now() - item.observedAt < maxWaitMs)
		if (pending.length === 0) clear()
		else timer = setTimeout(expire, Math.max(1, pending[0].observedAt + maxWaitMs - Date.now()))
	}

	return (event, properties) => {
		try {
			const currentTarget = getTarget()
			if (!currentTarget) return
			if (!options.hasConsent()) {
				clear()
				return
			}
			const item = {
				event,
				// Snapshot only supplied, sanitized event properties, never query state
				// or the browser URL. Caller mutation cannot change an observed action.
				properties: JSON.parse(JSON.stringify(sanitizeAnalyticsProperties(properties ?? {}))),
				observedAt: Date.now(),
			}
			if (options.isReady()) {
				flush()
				if (options.hasConsent()) send(item)
				return
			}
			pending = pending.filter((action) => Date.now() - action.observedAt < maxWaitMs)
			if (pending.length >= maxPending) return
			pending.push(item)
			if (!target) {
				target = currentTarget
				target.addEventListener('posthog-initialized', flush)
				target.addEventListener('cookie-consent-updated', onConsentUpdate)
			}
			if (timer === undefined) timer = setTimeout(expire, maxWaitMs)
		} catch {
			// No init API, serialization, cookie or listener failure may block an action.
		}
	}
}
